import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, User, Building2, Phone, Mail, MapPin, Save,
  Loader2, Home, Users, Shield, BellOff, Settings, History, GitMerge, Plus, FileText, Trash2
} from "lucide-react";
import { API } from "@/App";
import { ApplicantForm } from "@/components/ApplicantForm";
import { ApplicantMerge, ApplicantHistory } from "@/components/ApplicantMerge";
import { AddInstructionModal, InstructionCard } from "@/components/instructions/AddInstructionModal";

export const ApplicantDetailsPage = () => {
  const { applicantId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [applicant, setApplicant] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedApplicant, setEditedApplicant] = useState(null);
  
  // Merge & History modals
  const [showMerge, setShowMerge] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Property Instructions
  const [showAddInstruction, setShowAddInstruction] = useState(false);
  const [instructions, setInstructions] = useState([]);

  useEffect(() => {
    fetchApplicantDetails();
    fetchInstructions();
  }, [applicantId]);

  const fetchInstructions = async () => {
    try {
      const res = await axios.get(`${API}/instructions/by-applicant/${applicantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstructions(res.data.instructions || []);
    } catch (error) {
      console.error("Error fetching instructions:", error);
    }
  };

  const fetchApplicantDetails = async () => {
    try {
      setLoading(true);
      // Fetch applicant details
      const res = await axios.get(`${API}/applicants/details/${applicantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setApplicant(res.data.applicant);
      setCaseData(res.data.case);
      setEditedApplicant(res.data.applicant);
    } catch (error) {
      console.error("Error fetching applicant:", error);
      toast.error("Failed to load applicant details");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/applicants/sales/${applicantId}`, {
        applicant: editedApplicant
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Applicant details updated");
      setApplicant(editedApplicant);
      setEditMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update applicant");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    // Check if applicant has instructions
    const hasInstructions = instructions && instructions.length > 0;
    
    let confirmMessage = `Are you sure you want to delete "${getDisplayName()}"?\n\nThis action cannot be undone.`;
    
    if (hasInstructions) {
      confirmMessage = `⚠️ WARNING: "${getDisplayName()}" has ${instructions.length} property instruction(s).\n\nDeleting this applicant will ALSO delete:\n${instructions.map(i => `• ${i.address_line1 || i.property_address || i.full_address || 'Property'}`).join('\n')}\n\nAre you sure you want to continue?`;
    }
    
    if (!confirm(confirmMessage)) {
      return;
    }
    
    setDeleting(true);
    try {
      // Use force=true if there are instructions
      const url = hasInstructions 
        ? `${API}/applicants/sales/${applicantId}?force=true`
        : `${API}/applicants/sales/${applicantId}`;
        
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(hasInstructions 
        ? `Applicant and ${instructions.length} instruction(s) deleted successfully`
        : "Applicant deleted successfully"
      );
      navigate("/sales");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to delete applicant";
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const getDisplayName = () => {
    if (!applicant) return "Unknown";
    if (applicant.is_business && applicant.business_name) {
      return applicant.business_name;
    }
    const contacts = applicant.contacts || [];
    if (contacts.length === 0) return "Unknown";
    
    const names = contacts.map(c => {
      const title = c.title || "";
      return `${title} ${c.first_name} ${c.last_name}`.trim();
    });
    
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return names.slice(0, -1).join(", ") + ` & ${names[names.length - 1]}`;
  };

  const getPrimaryContact = () => {
    if (!applicant?.contacts?.[0]) return null;
    return applicant.contacts[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!applicant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Applicant not found</h2>
          <Button className="mt-4" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const primaryContact = getPrimaryContact();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  {applicant.is_business ? (
                    <Building2 className="w-6 h-6 text-white" />
                  ) : (
                    <User className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h1 className="text-xl font-semibold">{getDisplayName()}</h1>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">
                      {applicant.role}
                    </Badge>
                    {caseData && (
                      <span className="text-sm text-muted-foreground">
                        • {caseData.address_line1}, {caseData.postcode}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Button variant="outline" onClick={() => {
                    setEditMode(false);
                    setEditedApplicant(applicant);
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="default" 
                    onClick={() => setShowAddInstruction(true)} 
                    data-testid="add-property-btn"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Property
                  </Button>
                  <Button variant="outline" onClick={() => setShowHistory(true)} data-testid="history-btn">
                    <History className="w-4 h-4 mr-2" />
                    History
                  </Button>
                  <Button variant="outline" onClick={() => setShowMerge(true)} data-testid="merge-btn">
                    <GitMerge className="w-4 h-4 mr-2" />
                    Find Duplicates
                  </Button>
                  <Button onClick={() => setEditMode(true)}>
                    Edit Details
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                    data-testid="delete-applicant-btn"
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {editMode ? (
          /* Edit Mode - Full Form */
          <Card>
            <CardHeader>
              <CardTitle>Edit Applicant Details</CardTitle>
            </CardHeader>
            <CardContent>
              <ApplicantForm
                value={editedApplicant}
                onChange={setEditedApplicant}
                label={applicant.role}
                showBusinessOption={true}
                showCorrespondenceAddress={true}
                showStatusFields={true}
                showMarketingConsents={true}
                showReminderExclusions={true}
              />
            </CardContent>
          </Card>
        ) : (
          /* View Mode - Summary Cards */
          <div className="space-y-6">
            {/* Quick Info Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Primary Contact Card */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {primaryContact?.mobile_1 && (
                    <a href={`tel:${primaryContact.mobile_1}`} className="block text-blue-600 hover:underline">
                      {primaryContact.mobile_1}
                    </a>
                  )}
                  {primaryContact?.email_1 && (
                    <a href={`mailto:${primaryContact.email_1}`} className="block text-blue-600 hover:underline text-sm mt-1">
                      {primaryContact.email_1}
                    </a>
                  )}
                  {!primaryContact?.mobile_1 && !primaryContact?.email_1 && (
                    <span className="text-muted-foreground">No contact details</span>
                  )}
                </CardContent>
              </Card>

              {/* Address Card */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Address
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {applicant.address ? (
                    <div className="text-sm">
                      <p>{applicant.address.line_1}</p>
                      {applicant.address.line_2 && <p>{applicant.address.line_2}</p>}
                      <p>{applicant.address.town}, {applicant.address.postcode}</p>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">No address</span>
                  )}
                </CardContent>
              </Card>

              {/* Status Card */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {applicant.finance_status && (
                      <Badge variant="outline" className="mr-2">
                        {applicant.finance_status}
                      </Badge>
                    )}
                    {applicant.chain_status && (
                      <Badge variant="outline">
                        {applicant.chain_status}
                      </Badge>
                    )}
                    {!applicant.finance_status && !applicant.chain_status && (
                      <span className="text-muted-foreground text-sm">No status set</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Tabs defaultValue="instructions" className="space-y-4">
              <TabsList>
                <TabsTrigger value="instructions" data-testid="instructions-tab">
                  <FileText className="w-4 h-4 mr-2" />
                  Property Instructions
                  {instructions.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{instructions.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="details">
                  <Users className="w-4 h-4 mr-2" />
                  People & Address
                </TabsTrigger>
                <TabsTrigger value="status">
                  <Settings className="w-4 h-4 mr-2" />
                  Status & Assignment
                </TabsTrigger>
                <TabsTrigger value="consents">
                  <Shield className="w-4 h-4 mr-2" />
                  Consents
                </TabsTrigger>
                {caseData && (
                  <TabsTrigger value="case">
                    <Home className="w-4 h-4 mr-2" />
                    Linked Case
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Property Instructions Tab */}
              <TabsContent value="instructions">
                <div className="space-y-4">
                  {instructions.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Home className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">No Property Instructions</h3>
                        <p className="text-muted-foreground mb-4">
                          Click "Add Property" to create a new sales or lettings instruction
                        </p>
                        <Button onClick={() => setShowAddInstruction(true)} data-testid="add-first-property-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Property
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {instructions.map(instruction => (
                        <InstructionCard
                          key={instruction.instruction_id}
                          instruction={instruction}
                          onUpdate={fetchInstructions}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* People & Address Tab */}
              <TabsContent value="details">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* People */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        {applicant.is_business ? "Business & Contacts" : "People"}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {applicant.is_business && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 className="w-4 h-4 text-slate-600" />
                            <span className="font-medium">Business</span>
                          </div>
                          <p className="text-lg">{applicant.business_name}</p>
                        </div>
                      )}
                      
                      {applicant.contacts?.map((contact, index) => (
                        <div key={index} className="p-4 border rounded-lg space-y-2">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-600" />
                            <span className="font-medium">
                              {contact.title} {contact.first_name} {contact.last_name}
                            </span>
                            {contact.gender && (
                              <Badge variant="outline" className="text-xs">
                                {contact.gender}
                              </Badge>
                            )}
                          </div>
                          
                          {contact.friendly_name && (
                            <p className="text-sm text-muted-foreground">
                              Known as: {contact.friendly_name}
                            </p>
                          )}
                          
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {contact.mobile_1 && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                <a href={`tel:${contact.mobile_1}`} className="text-blue-600 hover:underline">
                                  {contact.mobile_1}
                                </a>
                              </div>
                            )}
                            {contact.mobile_2 && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                <a href={`tel:${contact.mobile_2}`} className="text-blue-600 hover:underline">
                                  {contact.mobile_2}
                                </a>
                              </div>
                            )}
                            {contact.home_tel && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                <span>{contact.home_tel} (Home)</span>
                              </div>
                            )}
                            {contact.work_tel && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                <span>{contact.work_tel} (Work)</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="space-y-1 text-sm">
                            {contact.email_1 && (
                              <div className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                <a href={`mailto:${contact.email_1}`} className="text-blue-600 hover:underline">
                                  {contact.email_1}
                                </a>
                                <Badge variant="outline" className="text-xs">Primary</Badge>
                              </div>
                            )}
                            {contact.email_2 && (
                              <div className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                <a href={`mailto:${contact.email_2}`} className="text-blue-600 hover:underline">
                                  {contact.email_2}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Addresses */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        Addresses
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {applicant.address && (
                        <div className="p-4 border rounded-lg">
                          <p className="text-xs text-muted-foreground mb-2">Main Address</p>
                          <p className="font-medium">{applicant.address.line_1}</p>
                          {applicant.address.line_2 && <p>{applicant.address.line_2}</p>}
                          <p>{applicant.address.town}</p>
                          {applicant.address.county && <p>{applicant.address.county}</p>}
                          <p className="font-medium">{applicant.address.postcode}</p>
                          <p className="text-sm text-muted-foreground">{applicant.address.country}</p>
                        </div>
                      )}
                      
                      {applicant.has_different_correspondence && applicant.correspondence_address && (
                        <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                          <p className="text-xs text-amber-700 mb-2">Correspondence Address</p>
                          <p className="font-medium">{applicant.correspondence_address.line_1}</p>
                          {applicant.correspondence_address.line_2 && <p>{applicant.correspondence_address.line_2}</p>}
                          <p>{applicant.correspondence_address.town}</p>
                          {applicant.correspondence_address.county && <p>{applicant.correspondence_address.county}</p>}
                          <p className="font-medium">{applicant.correspondence_address.postcode}</p>
                          <p className="text-sm text-muted-foreground">{applicant.correspondence_address.country}</p>
                        </div>
                      )}
                      
                      {!applicant.address && (
                        <p className="text-muted-foreground text-center py-4">No address on file</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Status & Assignment Tab */}
              <TabsContent value="status">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Status & Assignment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Lead Staff</p>
                          <p className="font-medium">{applicant.lead_staff || "Not assigned"}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Finance Status</p>
                          <p className="font-medium">{applicant.finance_status || "Not set"}</p>
                          {applicant.finance_status_notes && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {applicant.finance_status_notes}
                            </p>
                          )}
                        </div>
                        
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Chain Status</p>
                          <p className="font-medium">{applicant.chain_status || "Not set"}</p>
                          {applicant.chain_status_notes && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {applicant.chain_status_notes}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Branches</p>
                          <div className="flex flex-wrap gap-2">
                            {applicant.branches?.length > 0 ? (
                              applicant.branches.map(b => (
                                <Badge key={b} variant="outline" className="capitalize">
                                  {b.replace("_", " ")}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-muted-foreground">No branches selected</span>
                            )}
                          </div>
                        </div>
                        
                        {applicant.notes && (
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Notes</p>
                            <p className="p-3 bg-slate-50 rounded-lg text-sm">
                              {applicant.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Consents Tab */}
              <TabsContent value="consents">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Shield className="w-5 h-5" />
                        Marketing Consent (GDPR)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span>Property details via email</span>
                        <Badge variant={applicant.consent_property_email ? "default" : "secondary"}>
                          {applicant.consent_property_email ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Property details via SMS</span>
                        <Badge variant={applicant.consent_property_sms ? "default" : "secondary"}>
                          {applicant.consent_property_sms ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Marketing via email</span>
                        <Badge variant={applicant.consent_marketing_email ? "default" : "secondary"}>
                          {applicant.consent_marketing_email ? "Yes" : "No"}
                        </Badge>
                      </div>
                      
                      {applicant.consent_method && (
                        <div className="pt-2 border-t">
                          <p className="text-sm text-muted-foreground">Consent Method</p>
                          <p className="font-medium">{applicant.consent_method}</p>
                        </div>
                      )}
                      
                      {applicant.consent_notes && (
                        <div className="pt-2 border-t">
                          <p className="text-sm text-muted-foreground">Consent Notes</p>
                          <p className="text-sm">{applicant.consent_notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BellOff className="w-5 h-5" />
                        Reminder Exclusions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Viewing Reminders</p>
                        <div className="flex gap-2">
                          <Badge variant={applicant.exclude_viewing_email ? "destructive" : "secondary"}>
                            Email: {applicant.exclude_viewing_email ? "Excluded" : "Included"}
                          </Badge>
                          <Badge variant={applicant.exclude_viewing_sms ? "destructive" : "secondary"}>
                            SMS: {applicant.exclude_viewing_sms ? "Excluded" : "Included"}
                          </Badge>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Appointment Reminders</p>
                        <div className="flex gap-2">
                          <Badge variant={applicant.exclude_appointment_email ? "destructive" : "secondary"}>
                            Email: {applicant.exclude_appointment_email ? "Excluded" : "Included"}
                          </Badge>
                          <Badge variant={applicant.exclude_appointment_sms ? "destructive" : "secondary"}>
                            SMS: {applicant.exclude_appointment_sms ? "Excluded" : "Included"}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Linked Case Tab */}
              {caseData && (
                <TabsContent value="case">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Home className="w-5 h-5" />
                        Linked Sales Case
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div 
                        className="p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => navigate(`/sales/case/${caseData.case_id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{caseData.address_line1}</h3>
                            <p className="text-muted-foreground">{caseData.postcode}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline" className="capitalize">
                              {caseData.status?.replace("_", " ")}
                            </Badge>
                            <p className="text-lg font-bold text-emerald-600 mt-1">
                              £{caseData.offer_price?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}
            </Tabs>
          </div>
        )}
      </main>

      {/* Merge Modal */}
      <ApplicantMerge
        open={showMerge}
        onOpenChange={setShowMerge}
        applicantId={applicantId}
        applicantName={getDisplayName()}
        onMergeComplete={(primaryId) => {
          // If we merged into a different record, navigate to it
          if (primaryId !== applicantId) {
            navigate(`/sales/applicant/${primaryId}`);
          } else {
            fetchApplicantDetails();
          }
        }}
      />

      {/* History Modal */}
      <ApplicantHistory
        open={showHistory}
        onOpenChange={setShowHistory}
        applicantId={applicantId}
        applicantName={getDisplayName()}
      />

      {/* Add Property Instruction Modal */}
      <AddInstructionModal
        open={showAddInstruction}
        onOpenChange={setShowAddInstruction}
        applicantId={applicantId}
        applicantName={getDisplayName()}
        onSuccess={() => {
          fetchInstructions();
        }}
      />
    </div>
  );
};

export default ApplicantDetailsPage;
